import {
  ComputeApiError,
  computeRequest,
} from "@/components/personal-apps/computeClient";

export type ComputePolicy = {
  max_price_per_hour_cents: number;
  max_concurrency: number;
  cloud_preference: "COMMUNITY" | "SECURE";
};
export type ComputeConnection = ComputePolicy & {
  id: string;
  provider: "runpod";
  provider_account_id: string;
  status: "active" | "disconnecting" | "disconnected";
  credential_health: "valid" | "invalid" | "unknown";
  revision: number;
  last_verified_at: string | null;
  last_error_code: string | null;
  read_verified: boolean;
  write_permissions_verified: boolean;
  live_provisions: {
    id: string;
    state: string;
    provider_resource_id: string | null;
    resolved_price_per_hour_cents: number | null;
  }[];
  estimate: {
    container_disk_gb: number | null;
    disk_cents_per_hour: number | null;
    total_at_gpu_ceiling_cents_per_hour: number | null;
    reference_only: boolean;
    pricing_url: string;
  };
};
const base = "/v1/compute-connections";
function connectionEndpointUnavailable(error: unknown): boolean {
  return (
    error instanceof ComputeApiError &&
    error.status === 404 &&
    (error.code === "not_found" || error.code === "http_404")
  );
}
export const computeConnectionsClient = {
  async list() {
    try {
      return await computeRequest<{
        operator_runpod?: {available: boolean; max_price_per_hour_cents: number|null; max_instance_seconds: number|null};
        available: boolean;
        connections: ComputeConnection[];
      }>(base);
    } catch (error) {
      // A previous API deployment has no connection route.
      if (connectionEndpointUnavailable(error))
        return { available: false, connections: [] };
      throw error;
    }
  },
  connect(input: ComputePolicy & { api_key: string; idempotency_key: string }) {
    return computeRequest<{ connection: ComputeConnection }>(base, {
      method: "POST",
      body: { ...input, provider: "runpod" },
      timeoutMs: 45000,
    });
  },
  update(c: ComputeConnection, policy: ComputePolicy) {
    return computeRequest<{ connection: ComputeConnection }>(
      `${base}/${encodeURIComponent(c.id)}`,
      { method: "PATCH", body: { ...policy, revision: c.revision } },
    );
  },
  replaceKey(c: ComputeConnection, api_key: string) {
    return computeRequest<{ connection: ComputeConnection }>(
      `${base}/${encodeURIComponent(c.id)}/credentials`,
      {
        method: "POST",
        body: { api_key, revision: c.revision },
        timeoutMs: 45000,
      },
    );
  },
  disconnect(c: ComputeConnection) {
    return computeRequest<{ connection: ComputeConnection }>(
      `${base}/${encodeURIComponent(c.id)}`,
      { method: "DELETE" },
    );
  },
};
export function computeConnectionError(error: unknown): string {
  const candidate = (error as { code?: unknown })?.code;
  // Older connection APIs return Zod's object-valued error. Do not render
  // its message or submitted values; recognize just the validation boundary.
  const legacyValidation =
    error instanceof ComputeApiError &&
    error.status === 400 &&
    candidate !== null &&
    typeof candidate === "object" &&
    (candidate as { name?: unknown }).name === "ZodError";
  const code = connectionEndpointUnavailable(error)
    ? "compute_connections_unavailable"
    : legacyValidation
      ? "compute_connection_invalid_input"
      : typeof candidate === "string"
        ? candidate
        : undefined;
  if (code === "compute_connection_invalid_input") {
    const fields =
      error instanceof ComputeApiError
        ? error.details.invalid_fields
        : undefined;
    if (Array.isArray(fields) && fields.includes("api_key"))
      return "Paste only the RunPod API key (8–1024 characters), then try again.";
    if (
      Array.isArray(fields) &&
      fields.some((field) =>
        [
          "max_price_per_hour_cents",
          "max_concurrency",
          "cloud_preference",
        ].includes(field),
      )
    )
      return "Check the GPU price limit, concurrent Pods and cloud settings, then try again.";
    return legacyValidation
      ? "Check the API key and compute settings, then try again."
      : "The connection request was not accepted. Refresh the page before trying again.";
  }
  const messages: Record<string, string> = {
    auth_required: "Sign in again before updating your compute connection.",
    no_session: "Sign in again before updating your compute connection.",
    http_401: "Sign in again before updating your compute connection.",
    compute_connection_required:
      "Connect your RunPod account in Compute settings.",
    compute_credential_invalid:
      "RunPod rejected the API key. Replace it with a valid key for the same account.",
    compute_permission_denied:
      "The RunPod key needs account and GPU catalog read access, plus Pods read, create and delete access.",
    compute_account_mismatch:
      "Use a key from the original RunPod account. Its Pods still need cleanup.",
    compute_connection_disconnecting:
      "Pod cleanup is in progress. New runs are paused.",
    compute_connection_revision_conflict:
      "The connection changed, or it still has a live Pod. Refresh before trying again.",
    compute_connection_exists:
      "A RunPod connection already exists. Refresh to view it.",
    idempotency_conflict: "This request changed. Refresh before trying again.",
    compute_connections_unavailable:
      "Compute connections are temporarily unavailable. Refresh the page before trying again.",
    compute_credential_unavailable:
      "The saved credential is unavailable. Replace the key in Compute settings.",
    compute_provider_unavailable: "RunPod could not be reached. Try again.",
    compute_no_offer_under_price_limit:
      "No available GPU meets this app's requirements and your price limit. Retry later or update the compute policy.",
    request_timeout:
      "The request timed out. Refresh to check whether it completed.",
    on_demand_machine_unavailable:
      "No available GPU meets this app's requirements and your price limit.",
    provision_capacity_exhausted:
      "The compute concurrency limit has been reached. Wait for Pod cleanup before retrying.",
  };
  return (
    messages[code ?? ""] ??
    "The compute connection could not be updated. Try again."
  );
}
export function safeComputeReturnPath(search: string) {
  const target = new URLSearchParams(search).get("return_to");
  return target && /^\/apps\/[a-zA-Z0-9_-]+$/.test(target) ? target : null;
}
